import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuthContext } from "@asgardeo/auth-react";
import { Helmet } from "react-helmet-async";
import { Link, useSearchParams } from "react-router-dom";
import DialogModal from "../../components/Modal/DialogModal";
import MainLayout from "../../layouts/MainLayout";
import { CHAT_DB_SERVER_URL } from "../../config/backend-endpoints";
import { ensureUtcIso } from "../../utils/dateTime";

const PRODUCT_FEATURES = [
  {
    n: "01",
    tag: "Research",
    title: "Researcher Silva 2, a deep legal research agent.",
    description:
      "Answers hard legal questions and cites the statutes and case law it relies on.",
  },
  {
    n: "02",
    tag: "Privacy",
    title: "Instant delete, with no server retention.",
    description:
      "Delete any chat or uploaded file at once, with nothing kept on the server. Temporary chats clear themselves within 24 hours.",
  },
  {
    n: "03",
    tag: "Sources",
    title: "Web search built on official Sri Lankan sources.",
    description:
      "Searches gazettes, regulations, and circulars from official government sites, so every answer traces back to the source of record.",
  },
  {
    n: "04",
    tag: "Translation",
    title: "Translator Siriwardena 1 for trilingual legal translation.",
    description:
      "Translates legal documents across Sinhala, Tamil, and English, with terms of art preserved in place.",
  },
  {
    n: "05",
    tag: "Coming soon",
    title: "Drafter and reviewer agents.",
    description:
      "Drafting and review agents alongside research and translation. Arriving soon.",
  },
];

const INCLUDED_ITEMS = [
  "Access to chat.paralegal.lk",
  "Ongoing platform and feature updates",
];

const GROUP_PLAN_HIGHLIGHTS = [
  "Multi-user access for legal teams",
  "Volume discounts that scale with seat count",
  "Coordinated onboarding and support",
];

const STATUS_OPTIONS = new Set([
  "active",
  "pending_activation",
  "activation_failed",
  "inactive",
  "manual_review",
  "payment_failed_grace",
  "pending_deactivation",
  "deactivation_failed",
  "trial_active",
  "trial_ending_soon",
  "trial_expired_access_paused",
  "trial_pending_activation",
  "trial_activation_failed",
  // Legacy URL/query compatibility states
  "success",
  "activating",
  "pending",
  "failed",
  "already_subscribed",
]);

const PLAN_CODES = {
  monthly: "CHAT_PRO_MONTHLY",
  yearly: "CHAT_PRO_YEARLY",
};

const ALLOWED_PLAN_CODES = new Set(Object.values(PLAN_CODES));
const TRIAL_PLAN_CODE = PLAN_CODES.monthly;
const PLAN_OPTIONS = [
  {
    key: "monthly",
    code: PLAN_CODES.monthly,
    planName: "Monthly Professional Plan",
    billingLabel: "Monthly billing",
    price: "Rs. 20,000",
    suffix: "/ month",
    description: "Monthly access for individual legal professionals and teams.",
  },
  {
    key: "yearly",
    code: PLAN_CODES.yearly,
    planName: "Yearly Professional Plan",
    billingLabel: "Yearly billing",
    price: "Rs. 220,000",
    suffix: "/ year",
    badge: "1 month free",
    valueLabel: "Best value",
    comparison: "Save Rs. 20,000 compared to monthly billing",
    valueNote: "Equivalent to approx. Rs. 18,333/month, billed annually",
    description: "Pay annually and get the equivalent of one month free.",
    // highlight: true,
  },
];
const PLAN_DISPLAY_LABELS = {
  [PLAN_CODES.monthly]: "Monthly",
  [PLAN_CODES.yearly]: "Yearly",
};

const planDisplayLabel = (planCode) => PLAN_DISPLAY_LABELS[planCode] || "Selected";

const normalizeSelectedTrialEndPlan = (payload) => {
  const planCode = String(payload?.selected_plan_code || "").trim();
  const status = String(payload?.selected_plan_status || "").trim().toLowerCase();
  const scheduled = Boolean(payload?.scheduled_for_trial_end);
  if (!scheduled || !ALLOWED_PLAN_CODES.has(planCode) || status !== "approved") {
    return {
      selectedPlanCode: null,
      selectedPlanStatus: null,
      scheduledForTrialEnd: false,
    };
  }
  return {
    selectedPlanCode: planCode,
    selectedPlanStatus: status,
    scheduledForTrialEnd: true,
  };
};

const TRIAL_LIFECYCLE_STATUS_OPTIONS = new Set([
  "trialing",
  "converted_to_paid",
  "expired",
  "access_removal_started",
]);

const LEGACY_STATUS_TO_CANONICAL = {
  success: "active",
  already_subscribed: "active",
  activating: "pending_activation",
  pending: "pending_activation",
  failed: "activation_failed",
};

const normalizeUiStatus = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) {
    return null;
  }
  if (LEGACY_STATUS_TO_CANONICAL[normalized]) {
    return LEGACY_STATUS_TO_CANONICAL[normalized];
  }
  if (STATUS_OPTIONS.has(normalized)) {
    return normalized;
  }
  return null;
};

const normalizeTrialLifecycleStatus = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) {
    return null;
  }
  if (TRIAL_LIFECYCLE_STATUS_OPTIONS.has(normalized)) {
    return normalized;
  }
  return null;
};

const toOptionalIsoDate = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized) {
    return null;
  }
  const parsed = new Date(ensureUtcIso(normalized));
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.toISOString();
};

const formatIstDateTime = (value) => {
  const iso = toOptionalIsoDate(value);
  if (!iso) {
    return "";
  }
  return new Intl.DateTimeFormat("en-LK", {
    timeZone: "Asia/Colombo",
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
};

const calculateTrialDaysRemaining = (trialEndsAtIso) => {
  if (!trialEndsAtIso) {
    return null;
  }
  const trialEndsAtMs = Date.parse(ensureUtcIso(trialEndsAtIso));
  if (Number.isNaN(trialEndsAtMs)) {
    return null;
  }
  const diffMs = trialEndsAtMs - Date.now();
  return Math.max(0, Math.ceil(diffMs / 86400000));
};

const normalizeTrialDaysRemaining = (value, trialEndsAtIso) => {
  if (value !== null && value !== undefined && value !== "") {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return Math.max(0, Math.ceil(numeric));
    }
  }
  return calculateTrialDaysRemaining(trialEndsAtIso);
};

const extractTrialProjection = (payload) => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  let trialStatus = normalizeTrialLifecycleStatus(payload?.trial_status);
  if (!trialStatus) {
    trialStatus = normalizeTrialLifecycleStatus(payload?.status);
  }

  const reason = String(payload?.reason || "").trim().toLowerCase();
  if (!trialStatus && reason === "trial_started_pending_activation") {
    trialStatus = "trialing";
  }

  let trialEndsAt = toOptionalIsoDate(payload?.trial_ends_at);
  if (!trialEndsAt && reason.startsWith("trial_")) {
    trialEndsAt = toOptionalIsoDate(payload?.effective_to);
  }

  const accessActionStatus = String(payload?.access_action_status || "").trim().toLowerCase() || null;
  const trialDaysRemaining = normalizeTrialDaysRemaining(payload?.trial_days_remaining, trialEndsAt);
  const backendEligible =
    typeof payload?.trial_eligible === "boolean" ? payload.trial_eligible : null;
  const existing = typeof payload?.existing === "boolean" ? payload.existing : null;

  const hasTrialFields =
    trialStatus !== null ||
    trialEndsAt !== null ||
    trialDaysRemaining !== null ||
    accessActionStatus !== null ||
    backendEligible !== null ||
    existing !== null;

  if (!hasTrialFields) {
    return null;
  }

  return {
    trialStatus,
    trialEndsAt,
    trialDaysRemaining,
    accessActionStatus,
    backendEligible,
    existing,
  };
};

const mergeTrialProjection = (previous, incoming) => {
  if (!incoming) {
    return previous;
  }
  if (!previous) {
    return incoming;
  }

  return {
    trialStatus: incoming.trialStatus || previous.trialStatus || null,
    trialEndsAt: incoming.trialEndsAt || previous.trialEndsAt || null,
    trialDaysRemaining:
      incoming.trialDaysRemaining !== null && incoming.trialDaysRemaining !== undefined
        ? incoming.trialDaysRemaining
        : previous.trialDaysRemaining,
    accessActionStatus: incoming.accessActionStatus || previous.accessActionStatus || null,
    backendEligible:
      incoming.backendEligible !== null && incoming.backendEligible !== undefined
        ? incoming.backendEligible
        : previous.backendEligible,
    existing:
      incoming.existing !== null && incoming.existing !== undefined
        ? incoming.existing
        : previous.existing,
  };
};

const deriveTrialUiStatus = (canonicalStatus, trialProjection) => {
  const trialStatus = normalizeTrialLifecycleStatus(trialProjection?.trialStatus);
  if (!trialStatus || trialStatus === "converted_to_paid") {
    return null;
  }

  if (trialStatus === "expired" || trialStatus === "access_removal_started") {
    return "trial_expired_access_paused";
  }

  const accessActionStatus = String(trialProjection?.accessActionStatus || "")
    .trim()
    .toLowerCase();
  if (
    canonicalStatus === "activation_failed" ||
    accessActionStatus === "pending_activation_enqueue_failed"
  ) {
    return "trial_activation_failed";
  }
  if (
    canonicalStatus === "pending_activation" ||
    accessActionStatus.startsWith("pending_activation")
  ) {
    return "trial_pending_activation";
  }

  if ((trialProjection?.trialDaysRemaining ?? null) !== null && trialProjection.trialDaysRemaining <= 2) {
    return "trial_ending_soon";
  }

  return "trial_active";
};

const resolveUiProjection = (payload, priorTrialProjection = null) => {
  const canonicalStatus = mapBackendProjectionToUiStatus(payload);
  let mergedTrialProjection = mergeTrialProjection(
    priorTrialProjection,
    extractTrialProjection(payload)
  );

  if (
    canonicalStatus === "active" &&
    String(mergedTrialProjection?.accessActionStatus || "").startsWith("pending_activation")
  ) {
    mergedTrialProjection = {
      ...mergedTrialProjection,
      accessActionStatus: null,
    };
  }

  const trialUiStatus = deriveTrialUiStatus(canonicalStatus, mergedTrialProjection);

  return {
    canonicalStatus,
    status: trialUiStatus || canonicalStatus,
    trialProjection: mergedTrialProjection,
  };
};

const describeTrialDaysRemaining = (daysRemaining) => {
  if (typeof daysRemaining !== "number" || !Number.isFinite(daysRemaining)) {
    return "";
  }
  if (daysRemaining <= 0) {
    return "Trial ends today.";
  }
  if (daysRemaining === 1) {
    return "1 day remaining in your trial.";
  }
  return `${daysRemaining} days remaining in your trial.`;
};

const buildStatusNotice = (status, trialProjection, fallbackMessage = "") => {
  const trialDays = describeTrialDaysRemaining(trialProjection?.trialDaysRemaining);

  if (status === "active") {
    return "This account already has active chat access.";
  }
  if (status === "trial_active") {
    return trialDays
      ? `${trialDays} Subscribe now to continue uninterrupted after your trial.`
      : "Your free trial is active. Subscribe now to continue uninterrupted.";
  }
  if (status === "trial_ending_soon") {
    return trialDays
      ? `${trialDays} Subscribe now so access continues after trial.`
      : "Your trial is ending soon. Subscribe now so access continues.";
  }
  if (status === "trial_pending_activation") {
    return "Trial started, access activation is in progress.";
  }
  if (status === "trial_activation_failed") {
    return "Trial started, but automatic activation failed. Please retry shortly or contact support.";
  }
  if (status === "trial_expired_access_paused") {
    return "Trial ended. Subscribe to restore access.";
  }
  if (status === "manual_review") {
    return "Your payment is under manual review. Access will activate after verification.";
  }
  if (status === "payment_failed_grace") {
    return "Payment failed. Access remains active while renewal retries continue. Update your saved card from Account.";
  }
  if (status === "pending_deactivation") {
    return "Payment failed and access is ending or being removed. Update your card or choose a plan to restore access.";
  }
  if (status === "pending_activation") {
    return "Payment is verified and access activation is in progress.";
  }
  if (status === "activation_failed") {
    return "We could not activate your access automatically. Please contact support.";
  }
  if (fallbackMessage) {
    return fallbackMessage;
  }
  return "";
};

const subscriptionMetadataFields = (payload) => [
  payload?.plan_code,
  payload?.subscription_plan_code,
  payload?.subscription?.plan_code,
  payload?.entitlement?.plan_code,
  payload?.billing_interval,
  payload?.billingInterval,
  payload?.subscription_billing_interval,
  payload?.subscription?.billing_interval,
  payload?.subscription?.billingInterval,
  payload?.entitlement?.billing_interval,
];

const normalizeSubscriptionBillingCycle = (payload) => {
  const planCode = String(
    payload?.plan_code ||
    payload?.subscription_plan_code ||
    payload?.subscription?.plan_code ||
    payload?.entitlement?.plan_code ||
    ""
  )
    .trim()
    .toUpperCase();

  if (planCode === PLAN_CODES.monthly) {
    return "Monthly";
  }
  if (planCode === PLAN_CODES.yearly) {
    return "Yearly";
  }

  const billingInterval = String(
    payload?.billing_interval ||
    payload?.billingInterval ||
    payload?.subscription_billing_interval ||
    payload?.subscription?.billing_interval ||
    payload?.subscription?.billingInterval ||
    payload?.entitlement?.billing_interval ||
    ""
  )
    .trim()
    .toLowerCase();

  if (["monthly", "month"].includes(billingInterval)) {
    return "Monthly";
  }
  if (["annual", "annually", "yearly", "year"].includes(billingInterval)) {
    return "Yearly";
  }
  return null;
};

const extractSubscriptionStatusMeta = (payload) => {
  const hasPlanMetadata = subscriptionMetadataFields(payload).some((value) =>
    String(value || "").trim()
  );
  const pendingPlanCode = String(
    payload?.pending_plan_code || payload?.subscription?.pending_plan_code || ""
  ).trim();
  const cancellationMode = String(
    payload?.cancellation_mode || payload?.subscription?.cancellation_mode || "none"
  )
    .trim()
    .toLowerCase();
  const cancelEffectiveAt = toOptionalIsoDate(
    payload?.cancel_effective_at || payload?.subscription?.cancel_effective_at
  );
  const currentPeriodEnd = toOptionalIsoDate(payload?.end_at || payload?.subscription?.end_at);
  return {
    hasPlanMetadata:
      hasPlanMetadata ||
      Boolean(pendingPlanCode) ||
      cancellationMode !== "none" ||
      Boolean(cancelEffectiveAt) ||
      Boolean(currentPeriodEnd),
    billingCycle: normalizeSubscriptionBillingCycle(payload),
    pendingPlanCode: ALLOWED_PLAN_CODES.has(pendingPlanCode) ? pendingPlanCode : null,
    pendingPlanChangeAt:
      toOptionalIsoDate(payload?.pending_plan_change_at || payload?.subscription?.pending_plan_change_at),
    cancellationMode,
    cancelEffectiveAt,
    currentPeriodEnd,
  };
};

const buildInlineSubscriptionStatusText = (status, subscriptionStatusMeta, trialProjection) => {
  if (status === "active") {
    if (subscriptionStatusMeta?.cancellationMode === "cancel_at_period_end") {
      return "Subscription ending";
    }
    return subscriptionStatusMeta?.billingCycle
      ? `Active subscription · ${subscriptionStatusMeta.billingCycle}`
      : "Active subscription";
  }
  if (status === "trial_active") {
    return "Free trial active";
  }
  if (status === "trial_ending_soon") {
    return "Trial ending soon";
  }
  if (status === "pending_activation" || status === "trial_pending_activation") {
    return "Activation in progress";
  }
  if (status === "manual_review") {
    return "Payment under review";
  }
  if (status === "payment_failed_grace") {
    return "Payment retrying";
  }
  if (status === "pending_deactivation") {
    return "Payment failed";
  }
  if (status === "activation_failed" || status === "trial_activation_failed") {
    return "Activation failed";
  }

  const trialStatus = normalizeTrialLifecycleStatus(trialProjection?.trialStatus);
  if (status === "trial_expired_access_paused" || trialStatus === "expired" || trialStatus === "access_removal_started") {
    return "Trial ended";
  }

  return "";
};

const mapBackendProjectionToUiStatus = (payload) => {
  const accessStatus = normalizeUiStatus(payload?.access_status);
  const status = normalizeUiStatus(payload?.status);
  const billingStatus = String(payload?.billing_status || "").trim().toLowerCase();
  const hasAccess = payload?.chat_access === true || accessStatus === "active" || status === "active";

  if (
    accessStatus === "manual_review" ||
    status === "manual_review" ||
    billingStatus === "manual_review"
  ) {
    return "manual_review";
  }

  if (billingStatus === "payment_failed") {
    return hasAccess ? "payment_failed_grace" : "pending_deactivation";
  }

  if (
    accessStatus === "pending_deactivation" ||
    status === "pending_deactivation" ||
    accessStatus === "deactivation_failed" ||
    status === "deactivation_failed"
  ) {
    return "pending_deactivation";
  }

  if (accessStatus === "activation_failed" || status === "activation_failed") {
    return "activation_failed";
  }

  if (accessStatus === "pending_activation" || status === "pending_activation") {
    return "pending_activation";
  }

  if (
    billingStatus === "pending_payment" ||
    billingStatus === "payment_verified" ||
    billingStatus === "subscription_active"
  ) {
    return hasAccess ? "active" : "pending_activation";
  }

  if (hasAccess) {
    return "active";
  }

  if (accessStatus === "inactive" || status === "inactive") {
    return "inactive";
  }

  if (billingStatus === "cancelled" || billingStatus === "expired") {
    return "inactive";
  }

  // Conservative default: never claim active for unknown state.
  return "inactive";
};

const STATUS_POLL_INTERVAL_MS = 3000;
const STATUS_POLL_MAX_ATTEMPTS = 20;

const chatUrl =
  import.meta.env.VITE_CHAT_DOMAIN_URL || "https://chat.paralegal.lk";
const chatDomainLabel = chatUrl.replace(/^https?:\/\//, "").replace(/\/$/, "");
const chatDbServerUrl = CHAT_DB_SERVER_URL;

const submitPayHerePreapprovalForm = ({ actionUrl, formPayload, method = "POST" }) => {
  const form = document.createElement("form");
  form.method = String(method || "POST").toUpperCase();
  form.action = actionUrl;
  form.style.display = "none";

  Object.entries(formPayload || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = key;
    input.value = String(value);
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
};

const ChatPricing = () => {
  const { state, signIn, getAccessToken } = useAuthContext();
  const [searchParams] = useSearchParams();
  const [modalState, setModalState] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingPlanCode, setProcessingPlanCode] = useState(null);
  const [isStartingTrial, setIsStartingTrial] = useState(false);
  const [isCheckingEntitlement, setIsCheckingEntitlement] = useState(false);
  const [isPollingStatus, setIsPollingStatus] = useState(false);
  const [planChangeState, setPlanChangeState] = useState(null);
  const [isChangingPlan, setIsChangingPlan] = useState(false);
  const [runtimeStatus, setRuntimeStatus] = useState(null);
  const [trialProjection, setTrialProjection] = useState(null);
  const [subscriptionStatusMeta, setSubscriptionStatusMeta] = useState({
    billingCycle: null,
    pendingPlanCode: null,
    pendingPlanChangeAt: null,
    cancellationMode: "none",
    cancelEffectiveAt: null,
    currentPeriodEnd: null,
  });
  const [selectedTrialEndPlan, setSelectedTrialEndPlan] = useState({
    selectedPlanCode: null,
    selectedPlanStatus: null,
    scheduledForTrialEnd: false,
  });
  const [pendingTrialPlanCode, setPendingTrialPlanCode] = useState(null);
  const [pendingDirectPlanCode, setPendingDirectPlanCode] = useState(null); // Add this line
  const [activationNotice, setActivationNotice] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const pollingIntervalRef = useRef(null);
  const inFlightPollRef = useRef(false);
  const trialProjectionRef = useRef(null);

  const statusFromQuery = useMemo(() => {
    const value = searchParams.get("status");
    return normalizeUiStatus(value);
  }, [searchParams]);
  const orderIdFromQuery = useMemo(() => searchParams.get("order_id"), [searchParams]);
  const status = runtimeStatus || statusFromQuery;
  const isPaidActiveState = status === "active";
  const isPaymentFailedState = status === "payment_failed_grace" || status === "pending_deactivation";
  const canOpenChat = status === "active" || status === "payment_failed_grace" || status === "trial_active" || status === "trial_ending_soon";

  useEffect(() => {
    trialProjectionRef.current = trialProjection;
  }, [trialProjection]);


  const buildStatusUrl = (nextStatus, orderId = null) => {
    const url = new URL(window.location.href);
    url.searchParams.set("status", nextStatus);
    if (orderId) {
      url.searchParams.set("order_id", orderId);
    }
    return url.toString();
  };

  const updateStatusInUrl = useCallback((nextStatus, orderId = null) => {
    const url = new URL(window.location.href);
    url.searchParams.set("status", nextStatus);
    if (orderId) {
      url.searchParams.set("order_id", orderId);
    } else {
      url.searchParams.delete("order_id");
    }
    window.history.replaceState({}, "", url.toString());
  }, []);

  const stopStatusPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      window.clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    inFlightPollRef.current = false;
    setIsPollingStatus(false);
  }, []);

  const updateSubscriptionStatusMeta = useCallback((payload) => {
    const nextMeta = extractSubscriptionStatusMeta(payload);
    if (nextMeta.hasPlanMetadata) {
      setSubscriptionStatusMeta((previous) => ({
        ...previous,
        billingCycle: nextMeta.billingCycle || previous.billingCycle || null,
        pendingPlanCode: nextMeta.pendingPlanCode || null,
        pendingPlanChangeAt: nextMeta.pendingPlanChangeAt || null,
        cancellationMode: nextMeta.cancellationMode || "none",
        cancelEffectiveAt: nextMeta.cancelEffectiveAt || null,
        currentPeriodEnd: nextMeta.currentPeriodEnd || null,
      }));
    }
  }, []);

  const updateSelectedTrialEndPlan = useCallback((payload) => {
    setSelectedTrialEndPlan(normalizeSelectedTrialEndPlan(payload));
  }, []);

  const startStatusPolling = useCallback(
    (accessToken, orderId) => {
      if (!orderId || !accessToken) {
        return;
      }

      stopStatusPolling();
      setIsPollingStatus(true);
      setRuntimeStatus("pending_activation");
      setActivationNotice("Payment received. Activating your chat access...");
      updateStatusInUrl("pending_activation", orderId);

      let attempts = 0;
      const pollStatus = async () => {
        if (inFlightPollRef.current) {
          return;
        }

        inFlightPollRef.current = true;
        attempts += 1;

        try {
          const res = await fetch(
            `${chatDbServerUrl}/payment/status?order_id=${encodeURIComponent(orderId)}`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

          const payload = await res.json().catch(() => ({}));
          updateSubscriptionStatusMeta(payload);

          if (res.status === 404) {
            return;
          }

          if (!res.ok) {
            throw new Error(payload?.detail || payload?.error || `HTTP ${res.status}`);
          }

          const resolved = resolveUiProjection(payload, trialProjectionRef.current);
          if (resolved.trialProjection) {
            trialProjectionRef.current = resolved.trialProjection;
            setTrialProjection(resolved.trialProjection);
          }
          const mappedStatus = resolved.status;

          if (mappedStatus === "active" || mappedStatus === "trial_active" || mappedStatus === "trial_ending_soon") {
            stopStatusPolling();
            setRuntimeStatus(mappedStatus);
            setActivationNotice(buildStatusNotice(mappedStatus, resolved.trialProjection));
            updateStatusInUrl(mappedStatus, orderId);
            return;
          }

          if (mappedStatus === "payment_failed_grace" || mappedStatus === "pending_deactivation") {
            stopStatusPolling();
            setRuntimeStatus(mappedStatus);
            setActivationNotice(buildStatusNotice(mappedStatus, resolved.trialProjection));
            updateStatusInUrl(mappedStatus, orderId);
            return;
          }

          if (mappedStatus === "manual_review") {
            stopStatusPolling();
            setRuntimeStatus("manual_review");
            setActivationNotice(
              "Payment was received and is under review. Access will activate after manual verification."
            );
            updateStatusInUrl("manual_review", orderId);
            return;
          }

          if (mappedStatus === "activation_failed" || mappedStatus === "trial_activation_failed") {
            stopStatusPolling();
            setRuntimeStatus(mappedStatus);
            setActivationNotice(buildStatusNotice(mappedStatus, resolved.trialProjection));
            updateStatusInUrl(mappedStatus, orderId);
            return;
          }

          if (mappedStatus === "inactive" || mappedStatus === "trial_expired_access_paused") {
            stopStatusPolling();
            setRuntimeStatus(mappedStatus);
            setActivationNotice(buildStatusNotice(mappedStatus, resolved.trialProjection));
            updateStatusInUrl(mappedStatus, orderId);
            return;
          }

          if (mappedStatus === "pending_activation" || mappedStatus === "trial_pending_activation") {
            setRuntimeStatus(mappedStatus);
            setActivationNotice(buildStatusNotice(mappedStatus, resolved.trialProjection));
            updateStatusInUrl(mappedStatus, orderId);
            return;
          }

          if (payload?.status_code && String(payload.status_code) !== "2") {
            stopStatusPolling();
            setRuntimeStatus("activation_failed");
            setActivationNotice(buildStatusNotice("activation_failed", trialProjectionRef.current));
            updateStatusInUrl("activation_failed", orderId);
            return;
          }
        } catch (err) {
          console.error("Subscription status poll failed:", err);
          if (attempts >= STATUS_POLL_MAX_ATTEMPTS) {
            stopStatusPolling();
            setRuntimeStatus("pending_activation");
            setActivationNotice(
              "Payment is still being confirmed. Please refresh in a moment."
            );
            updateStatusInUrl("pending_activation", orderId);
          }
        } finally {
          inFlightPollRef.current = false;
        }

        if (attempts >= STATUS_POLL_MAX_ATTEMPTS) {
          stopStatusPolling();
          setRuntimeStatus("pending_activation");
          setActivationNotice(
            "Activation is taking longer than expected. We will keep your payment and complete access shortly."
          );
          updateStatusInUrl("pending_activation", orderId);
        }
      };

      pollingIntervalRef.current = window.setInterval(pollStatus, STATUS_POLL_INTERVAL_MS);
      pollStatus();
    },
    [stopStatusPolling, updateStatusInUrl, updateSubscriptionStatusMeta]
  );

  const startEntitlementPolling = useCallback(
    (accessToken, pendingStatus = "pending_activation") => {
      if (!accessToken) {
        return;
      }

      stopStatusPolling();
      setIsPollingStatus(true);
      setRuntimeStatus(pendingStatus);
      setActivationNotice(buildStatusNotice(pendingStatus, trialProjectionRef.current));
      updateStatusInUrl(pendingStatus);

      let attempts = 0;
      const pollEntitlement = async () => {
        if (inFlightPollRef.current) {
          return;
        }

        inFlightPollRef.current = true;
        attempts += 1;

        try {
          const res = await fetch(`${chatDbServerUrl}/chat-entitlements/me`, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });
          const payload = await res.json().catch(() => ({}));
          updateSubscriptionStatusMeta(payload);

          if (!res.ok) {
            throw new Error(payload?.detail || payload?.error || `HTTP ${res.status}`);
          }

          const resolved = resolveUiProjection(payload, trialProjectionRef.current);
          if (resolved.trialProjection) {
            trialProjectionRef.current = resolved.trialProjection;
            setTrialProjection(resolved.trialProjection);
          }

          const mappedStatus = resolved.status;
          if (
            mappedStatus === "active" ||
            mappedStatus === "trial_active" ||
            mappedStatus === "trial_ending_soon" ||
            mappedStatus === "manual_review" ||
            mappedStatus === "payment_failed_grace" ||
            mappedStatus === "pending_deactivation" ||
            mappedStatus === "activation_failed" ||
            mappedStatus === "trial_activation_failed" ||
            mappedStatus === "inactive" ||
            mappedStatus === "trial_expired_access_paused"
          ) {
            stopStatusPolling();
            setRuntimeStatus(mappedStatus);
            setActivationNotice(buildStatusNotice(mappedStatus, resolved.trialProjection));
            updateStatusInUrl(mappedStatus);
            return;
          }

          if (mappedStatus === "pending_activation" || mappedStatus === "trial_pending_activation") {
            setRuntimeStatus(mappedStatus);
            setActivationNotice(buildStatusNotice(mappedStatus, resolved.trialProjection));
            updateStatusInUrl(mappedStatus);
            return;
          }
        } catch (err) {
          console.error("Entitlement status poll failed:", err);
          if (attempts >= STATUS_POLL_MAX_ATTEMPTS) {
            stopStatusPolling();
            setRuntimeStatus(pendingStatus);
            setActivationNotice(
              "Access activation is taking longer than expected. Please refresh in a moment."
            );
            updateStatusInUrl(pendingStatus);
          }
        } finally {
          inFlightPollRef.current = false;
        }

        if (attempts >= STATUS_POLL_MAX_ATTEMPTS) {
          stopStatusPolling();
          setRuntimeStatus(pendingStatus);
          setActivationNotice(
            "Activation is taking longer than expected. We will complete access shortly."
          );
          updateStatusInUrl(pendingStatus);
        }
      };

      pollingIntervalRef.current = window.setInterval(pollEntitlement, STATUS_POLL_INTERVAL_MS);
      pollEntitlement();
    },
    [stopStatusPolling, updateStatusInUrl, updateSubscriptionStatusMeta]
  );

  const triggerStatusPolling = useCallback(
    async (orderId) => {
      try {
        const token = await getAccessToken();
        if (!token) {
          throw new Error("Authentication token is missing.");
        }
        startStatusPolling(token, orderId);
      } catch (err) {
        console.error("Failed to start status polling:", err);
        setActivationNotice(
          "Payment completed. We could not auto-refresh access status. Please refresh this page."
        );
      }
    },
    [getAccessToken, startStatusPolling]
  );

  const triggerEntitlementPolling = useCallback(
    async (pendingStatus = "pending_activation") => {
      try {
        const token = await getAccessToken();
        if (!token) {
          throw new Error("Authentication token is missing.");
        }
        startEntitlementPolling(token, pendingStatus);
      } catch (err) {
        console.error("Failed to start entitlement polling:", err);
        setActivationNotice(
          "Activation is in progress. We could not auto-refresh access status. Please refresh this page."
        );
      }
    },
    [getAccessToken, startEntitlementPolling]
  );

  const handleStartTrial = async () => {
    if (isStartingTrial || isProcessing || isPollingStatus) {
      return;
    }

    if (!state?.isAuthenticated) {
      setModalState("auth_required");
      return;
    }

    setIsStartingTrial(true);
    setErrorMessage("");
    setActivationNotice("");

    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error("Authentication token is missing.");
      }

      const trialResponse = await fetch(`${chatDbServerUrl}/chat-trials/start`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ plan_code: TRIAL_PLAN_CODE }),
      });

      const trialPayload = await trialResponse.json().catch(() => ({}));
      if (!trialResponse.ok) {
        throw new Error(trialPayload?.detail || "Failed to start free trial.");
      }

      const resolved = resolveUiProjection(trialPayload, trialProjection);
      if (resolved.trialProjection) {
        trialProjectionRef.current = resolved.trialProjection;
        setTrialProjection(resolved.trialProjection);
      }
      setRuntimeStatus(resolved.status);
      updateStatusInUrl(resolved.status);
      setActivationNotice(
        String(trialPayload?.message || "").trim() ||
        buildStatusNotice(resolved.status, resolved.trialProjection)
      );
      if (resolved.status === "pending_activation" || resolved.status === "trial_pending_activation") {
        startEntitlementPolling(accessToken, resolved.status);
      }
    } catch (error) {
      console.error("Trial start error:", error);
      setErrorMessage(error.message || "Failed to start free trial.");
      setModalState("trial_error");
    } finally {
      setIsStartingTrial(false);
    }
  };

  const handleStartSubscription = async (planCode, options = {}) => {
    const confirmedTrialSelection = Boolean(options.confirmedTrialSelection);
    const confirmedDirectSubscription = Boolean(options.confirmedDirectSubscription);

    if (isPaidActiveState && isCancelingAtPeriodEnd) {
      setActivationNotice(
        cancelEffectiveLabel
          ? `Your subscription will end on ${cancelEffectiveLabel}. Resume from Account before that date to keep access.`
          : "Your subscription is scheduled to end at the current period end. Resume from Account to keep access."
      );
      return;
    }

    if (isPaidActiveState) {
      const planDetails = PLAN_OPTIONS.find((plan) => plan.code === planCode);
      if (!planDetails) {
        setErrorMessage("Please choose a valid subscription plan.");
        setModalState("payment_error");
        return;
      }

      setPlanChangeState({
        newPlanCode: planCode,
        planName: planDetails.billingLabel,
      });
      return;
    }

    if (isProcessing || isStartingTrial || isPollingStatus) {
      return;
    }
    // # add new endpoint for manually change the plan
    // if (isProcessing || isStartingTrial || isPollingStatus || isPaidActiveState) {
    //   return;
    // }

    if (!ALLOWED_PLAN_CODES.has(planCode)) {
      setErrorMessage("Please choose a valid subscription plan.");
      setModalState("payment_error");
      return;
    }

    if (!state?.isAuthenticated) {
      setModalState("auth_required");
      return;
    }

    if (isTrialActiveOrEndingState) {
      if (selectedTrialEndPlan.selectedPlanCode === planCode) {
        return;
      }
      if (!confirmedTrialSelection) {
        setPendingTrialPlanCode(planCode);
        setModalState("confirm_trial_plan_selection");
        return;
      }
    } else {
      // Add this new else block
      if (!confirmedDirectSubscription) {
        setPendingDirectPlanCode(planCode);
        setModalState("confirm_direct_subscription");
        return;
      }
    }

    setIsProcessing(true);
    setProcessingPlanCode(planCode);
    setErrorMessage("");
    setActivationNotice("");

    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error("Authentication token is missing.");
      }

      const entitlementResponse = await fetch(
        `${chatDbServerUrl}/chat-entitlements/me`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const entitlementData = await entitlementResponse.json().catch(() => ({}));
      const entitlementResolved = resolveUiProjection(entitlementData, trialProjection);
      const entitlementStatus = entitlementResolved.status;
      if (entitlementResolved.trialProjection) {
        trialProjectionRef.current = entitlementResolved.trialProjection;
        setTrialProjection(entitlementResolved.trialProjection);
      }
      if (entitlementResponse.ok && entitlementStatus === "active") {
        setIsProcessing(false);
        setProcessingPlanCode(null);
        setRuntimeStatus("active");
        updateStatusInUrl("active");
        setActivationNotice("This account already has an active paid subscription.");
        return;
      }

      const isEntitlementTrialActiveOrEnding =
        entitlementStatus === "trial_active" || entitlementStatus === "trial_ending_soon";
      if (isEntitlementTrialActiveOrEnding && !confirmedTrialSelection && !isTrialActiveOrEndingState) {
        setPendingTrialPlanCode(planCode);
        setModalState("confirm_trial_plan_selection");
        setIsProcessing(false);
        setProcessingPlanCode(null);
        return;
      }
      const subscriptionIntent = isEntitlementTrialActiveOrEnding
        ? "trial_end_selection"
        : "direct_subscribe";

      const savedCardResponse = await fetch(
        `${chatDbServerUrl}/chat-subscriptions/saved-card/start`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            plan_code: planCode,
            intent: subscriptionIntent,
          }),
        }
      );

      const savedCardData = await savedCardResponse.json().catch(() => ({}));
      if (!savedCardResponse.ok) {
        throw new Error(
          savedCardData?.detail || "Failed to start saved-card subscription."
        );
      }

      if (savedCardData?.status !== "payment_method_required") {
        const savedStatus = String(savedCardData?.status || "").trim();
        if (savedStatus === "selected_for_trial_end" || savedStatus === "already_selected_for_trial_end") {
          const selectedStatus = entitlementStatus === "trial_ending_soon" ? "trial_ending_soon" : "trial_active";
          const selectedPlanCode = String(savedCardData?.selected_plan_code || savedCardData?.plan_code || planCode).trim();
          setSelectedTrialEndPlan(
            normalizeSelectedTrialEndPlan({
              selected_plan_code: selectedPlanCode,
              selected_plan_status: savedCardData?.selected_plan_status || "approved",
              scheduled_for_trial_end: true,
            })
          );
          setRuntimeStatus(selectedStatus);
          updateStatusInUrl(selectedStatus);
          setActivationNotice(
            `${planDisplayLabel(selectedPlanCode)} plan selected for trial-end billing`
          );
          setPendingTrialPlanCode(null);
          setIsProcessing(false);
          setProcessingPlanCode(null);
          return;
        }

        if (savedStatus === "pending_activation") {
          setRuntimeStatus("pending_activation");
          setActivationNotice(
            savedCardData?.message ||
            "Saved primary card charged successfully. Activating your chat access..."
          );
          updateStatusInUrl("pending_activation", savedCardData?.order_id || null);
          setIsProcessing(false);
          setProcessingPlanCode(null);
          startEntitlementPolling(accessToken, "pending_activation");
          return;
        }

        if (savedStatus === "already_active") {
          setRuntimeStatus("active");
          updateStatusInUrl("active");
          setActivationNotice(savedCardData?.message || "This account already has an active paid subscription.");
          setIsProcessing(false);
          setProcessingPlanCode(null);
          return;
        }

        if (savedStatus === "charge_pending") {
          setRuntimeStatus("manual_review");
          updateStatusInUrl("manual_review", savedCardData?.order_id || null);
          setActivationNotice(
            savedCardData?.message ||
            "Saved primary card charge is pending provider confirmation. Access will not activate until payment succeeds."
          );
          setIsProcessing(false);
          setProcessingPlanCode(null);
          return;
        }

        setRuntimeStatus("activation_failed");
        updateStatusInUrl("activation_failed", savedCardData?.order_id || null);
        setErrorMessage(
          savedCardData?.message ||
          "Saved primary card charge did not complete. Add or update your card and try again."
        );
        setModalState("payment_error");
        setIsProcessing(false);
        setProcessingPlanCode(null);
        return;
      }

      const preapprovalReturnStatus = isEntitlementTrialActiveOrEnding
        ? entitlementStatus
        : "pending_activation";
      const preapprovalCancelStatus = isEntitlementTrialActiveOrEnding
        ? entitlementStatus
        : "inactive";

      const preapprovalResponse = await fetch(
        `${chatDbServerUrl}/payment-methods/preapproval/start`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            purpose: "subscribe",
            plan_code: planCode,
            card_role: "primary",
            intent: subscriptionIntent,
            return_url: buildStatusUrl(preapprovalReturnStatus),
            cancel_url: buildStatusUrl(preapprovalCancelStatus),
          }),
        }
      );

      const preapprovalData = await preapprovalResponse.json().catch(() => ({}));
      if (!preapprovalResponse.ok) {
        throw new Error(
          preapprovalData?.detail || "Failed to start subscription card approval."
        );
      }

      const preapprovalPlanCode = String(preapprovalData.plan_code || "").trim();
      if (!ALLOWED_PLAN_CODES.has(preapprovalPlanCode)) {
        throw new Error("Card approval returned an unsupported subscription plan.");
      }
      if (preapprovalPlanCode !== planCode) {
        throw new Error("Card approval returned a different subscription plan than requested.");
      }
      if (preapprovalData.card_role !== "primary") {
        throw new Error("Card approval did not return a primary payment method setup.");
      }
      if (!preapprovalData?.action_url || !preapprovalData?.form_payload) {
        throw new Error("Card approval did not return a PayHere form payload.");
      }

      setRuntimeStatus(preapprovalReturnStatus);
      setActivationNotice(
        isEntitlementTrialActiveOrEnding
          ? "Redirecting to PayHere to approve your trial-end billing card. Your plan will start after the free trial."
          : "Redirecting to PayHere to approve your subscription payment method."
      );
      updateStatusInUrl(preapprovalReturnStatus, preapprovalData.order_id);
      submitPayHerePreapprovalForm({
        actionUrl: preapprovalData.action_url,
        formPayload: preapprovalData.form_payload,
        method: preapprovalData.method,
      });
    } catch (error) {
      console.error("Subscription preapproval error:", error);
      setErrorMessage(error.message || "Failed to start subscription card approval.");
      setModalState("payment_error");
      setIsProcessing(false);
      setProcessingPlanCode(null);
    }
  };

  const handleLogin = () => {
    sessionStorage.setItem(
      "redirectAfterLogin",
      `${window.location.pathname}${window.location.search}`
    );
    signIn();
  };

  useEffect(() => {
    let cancelled = false;

    const checkEntitlementOnLoad = async () => {
      if (!state?.isAuthenticated) {
        setIsCheckingEntitlement(false);
        return;
      }

      setIsCheckingEntitlement(true);
      try {
        const accessToken = await getAccessToken();
        if (!accessToken || cancelled) {
          return;
        }

        const res = await fetch(`${chatDbServerUrl}/chat-entitlements/me`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        const payload = await res.json().catch(() => ({}));
        updateSubscriptionStatusMeta(payload);

        if (!cancelled) {
          try {
            const subscriptionRes = await fetch(`${chatDbServerUrl}/chat-subscriptions/me`, {
              method: "GET",
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            });
            const subscriptionPayload = await subscriptionRes.json().catch(() => ({}));
            if (subscriptionRes.ok) {
              updateSubscriptionStatusMeta(subscriptionPayload);
              updateSelectedTrialEndPlan(subscriptionPayload);
            }
          } catch (subscriptionError) {
            console.error("Initial subscription selection check failed:", subscriptionError);
          }
        }

        if (cancelled || !res.ok) {
          return;
        }

        const resolved = resolveUiProjection(payload, trialProjection);
        if (resolved.trialProjection) {
          trialProjectionRef.current = resolved.trialProjection;
          setTrialProjection(resolved.trialProjection);
        }

        setRuntimeStatus(resolved.status);
        if (resolved.status === "active") {
          stopStatusPolling();
          updateStatusInUrl("active");
        }
        setActivationNotice(buildStatusNotice(resolved.status, resolved.trialProjection));
      } catch (error) {
        console.error("Initial entitlement check failed:", error);
      } finally {
        if (!cancelled) {
          setIsCheckingEntitlement(false);
        }
      }
    };

    checkEntitlementOnLoad();

    return () => {
      cancelled = true;
    };
  }, [
    state?.isAuthenticated,
    getAccessToken,
    stopStatusPolling,
    updateStatusInUrl,
    updateSubscriptionStatusMeta,
    updateSelectedTrialEndPlan,
  ]);

  useEffect(() => {
    return () => {
      stopStatusPolling();
    };
  }, [stopStatusPolling]);

  useEffect(() => {
    if (!state?.isAuthenticated) {
      return;
    }

    if (statusFromQuery !== "pending_activation" && statusFromQuery !== "trial_pending_activation") {
      return;
    }

    if (orderIdFromQuery) {
      triggerStatusPolling(orderIdFromQuery);
      return;
    }

    triggerEntitlementPolling(statusFromQuery);
  }, [
    state?.isAuthenticated,
    orderIdFromQuery,
    statusFromQuery,
    triggerStatusPolling,
    triggerEntitlementPolling,
  ]);

  const trialStatus = normalizeTrialLifecycleStatus(trialProjection?.trialStatus);
  const trialEligibleFromBackend = trialProjection?.backendEligible;
  const isAuthenticated = Boolean(state?.isAuthenticated);
  const trialAlreadyUsed =
    trialProjection?.accessActionStatus === "trial_already_used" ||
    trialStatus === "converted_to_paid" ||
    trialStatus === "expired" ||
    trialStatus === "access_removal_started" ||
    status === "trial_expired_access_paused";
  const isTrialActiveState = status === "trial_active";
  const isTrialEndingSoonState = status === "trial_ending_soon";
  const isTrialActiveOrEndingState = isTrialActiveState || isTrialEndingSoonState;
  const isTrialExpiredState = status === "trial_expired_access_paused";
  const isPendingActivationState =
    status === "pending_activation" || status === "trial_pending_activation";
  const isManualReviewState = status === "manual_review";
  const isActivationFailedState =
    status === "activation_failed" || status === "trial_activation_failed";
  const trialIneligibleFromBackend = trialEligibleFromBackend === false && !isPaidActiveState;
  const isTrialExpiredOrUsedState =
    isTrialExpiredState || trialAlreadyUsed || trialIneligibleFromBackend;
  const isTrialEligibleState =
    isAuthenticated &&
    trialEligibleFromBackend === true &&
    !isPaidActiveState &&
    !isPendingActivationState &&
    !isManualReviewState &&
    !isActivationFailedState &&
    !isPaymentFailedState &&
    !trialAlreadyUsed &&
    !isTrialActiveOrEndingState;
  const selectedTrialEndPlanCode = selectedTrialEndPlan.scheduledForTrialEnd
    ? selectedTrialEndPlan.selectedPlanCode
    : null;
  const selectedTrialEndPlanLabel = selectedTrialEndPlanCode
    ? planDisplayLabel(selectedTrialEndPlanCode)
    : null;
  const trialPrimaryMessage = selectedTrialEndPlanLabel
    ? `${selectedTrialEndPlanLabel} plan selected for trial-end billing.`
    : isTrialEndingSoonState
      ? "Approve a card before your trial ends. No full charge happens during the active trial."
      : "Approve a card now to continue after trial. No full charge happens during the active trial.";
  const isBusy = isCheckingEntitlement || isProcessing || isStartingTrial || isPollingStatus;
  const isCancelingAtPeriodEnd =
    subscriptionStatusMeta?.cancellationMode === "cancel_at_period_end";
  const cancelEffectiveLabel = formatIstDateTime(
    subscriptionStatusMeta?.cancelEffectiveAt || subscriptionStatusMeta?.currentPeriodEnd
  );
  const scheduledPaidPlanChangeLabel =
    !isCancelingAtPeriodEnd && subscriptionStatusMeta?.pendingPlanCode
      ? planDisplayLabel(subscriptionStatusMeta.pendingPlanCode)
      : null;
  const inlineStatusText = buildInlineSubscriptionStatusText(status, subscriptionStatusMeta, trialProjection);
  const showInlineStatusBadge = isAuthenticated && Boolean(inlineStatusText) && !isTrialEligibleState;
  const trialActionDisabled = isBusy;
  const subscriptionActionDisabled = isBusy || isCancelingAtPeriodEnd;

  const isSubscriptionChoiceState =
    isTrialEligibleState || isTrialActiveOrEndingState || isTrialExpiredOrUsedState || isActivationFailedState || isPaymentFailedState || isPaidActiveState;
  const showPlanCheckoutActions =
    (!isAuthenticated) ||
    (isSubscriptionChoiceState &&
      !isPendingActivationState &&
      !isManualReviewState);

  let planPrimaryLabel = null;
  let planPrimaryDisabled = false;
  let planPrimaryAction = null;
  let planPrimaryHref = null;
  let planPrimaryAriaLabel = "Subscription action";
  let planHelperText = activationNotice || "";
  const showAccountDetailsLink =
    isAuthenticated &&
    (
      isPaidActiveState ||
      isPaymentFailedState ||
      isPendingActivationState ||
      isManualReviewState ||
      isActivationFailedState ||
      isTrialActiveOrEndingState ||
      isTrialExpiredOrUsedState
    );

  if (!isAuthenticated) {
    planPrimaryLabel = "Start 30-Day Free Trial";
    planPrimaryDisabled = trialActionDisabled;
    planPrimaryAction = handleStartTrial;
    planPrimaryAriaLabel = "Start 30-day free trial";
    planHelperText = "Login required to start trial. You can compare monthly and yearly pricing below.";
  } else if (status === "payment_failed_grace") {
    planPrimaryLabel = "Open Chat";
    planPrimaryDisabled = false;
    planPrimaryAction = null;
    planPrimaryHref = chatUrl;
    planPrimaryAriaLabel = "Open chat workspace";
    planHelperText = "Payment failed. Access remains active while renewal retries continue. Update your saved card from Account.";
  } else if (status === "pending_deactivation") {
    planHelperText = "Payment failed and access is ending or being removed. Update your card from Account or choose a plan to restore access.";
  } else if (isPaidActiveState) {
    planPrimaryLabel = "Open Chat";
    planPrimaryDisabled = false;
    planPrimaryAction = null;
    planPrimaryHref = chatUrl;
    planPrimaryAriaLabel = "Open chat workspace";
    planHelperText = isCancelingAtPeriodEnd
      ? cancelEffectiveLabel
        ? `Your subscription will end on ${cancelEffectiveLabel}. Resume from Account to keep access after that date.`
        : "Your subscription is scheduled to end at the current period end. Resume from Account to keep access."
      : scheduledPaidPlanChangeLabel
        ? `Your plan will change to ${scheduledPaidPlanChangeLabel} at your next renewal.`
        : "Your subscription is active.";
  } else if (isPendingActivationState) {
    planPrimaryLabel = "Activating...";
    planPrimaryDisabled = true;
    planHelperText = "Access activation is in progress.";
  } else if (isManualReviewState) {
    planHelperText = "Payment is under review. Access will activate after verification.";
  } else if (isActivationFailedState) {
    planHelperText =
      "Activation failed. Choose a plan to retry subscription card approval, or contact support if the issue continues.";
  } else if (isTrialActiveOrEndingState) {
    planPrimaryLabel = "Open Chat";
    planPrimaryHref = chatUrl;
    planPrimaryAriaLabel = "Open chat workspace";
    planHelperText = trialPrimaryMessage;
  } else if (isTrialExpiredOrUsedState) {
    planHelperText = "Free trial already used for this account. Approve a monthly or yearly subscription card to restore access after a confirmed charge.";
  } else if (isTrialEligibleState) {
    planPrimaryLabel = isStartingTrial ? "Activating..." : "Start 30-Day Free Trial";
    planPrimaryDisabled = trialActionDisabled;
    planPrimaryAction = () => state?.isAuthenticated ? setModalState("confirm_start_trial") : setModalState("auth_required");
    planPrimaryAriaLabel = "Start 30-day free trial";
    planHelperText = "";
  } else if (isCheckingEntitlement) {
    planPrimaryLabel = "Checking Access...";
    planPrimaryDisabled = true;
    planPrimaryAction = null;
    planPrimaryAriaLabel = "Checking account access";
    planHelperText = "Checking your current access status.";
  }

  // add new endpoint for manually change the plan
  const executePlanChange = async () => {
    if (!planChangeState || isChangingPlan) {
      return;
    }

    setIsChangingPlan(true);
    setErrorMessage("");

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error("Authentication token is missing.");
      }

      const res = await fetch(`${chatDbServerUrl}/chat-subscriptions/change-plan`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ new_plan_code: planChangeState.newPlanCode }),
      });

      const payload = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(payload?.detail || "Failed to change plan.");
      }

      const pendingPlanCode = payload?.pending_plan_code || planChangeState.newPlanCode;
      const pendingLabel = planDisplayLabel(pendingPlanCode);
      setSubscriptionStatusMeta((previous) => ({
        ...previous,
        pendingPlanCode,
        pendingPlanChangeAt: toOptionalIsoDate(payload?.pending_plan_change_at),
      }));
      setRuntimeStatus("active");
      setActivationNotice(
        `Your plan will change to ${pendingLabel} at your next renewal.`
      );
      setPlanChangeState(null);
    } catch (error) {
      setErrorMessage(error?.message || "Could not update your plan. Please try again.");
    } finally {
      setIsChangingPlan(false);
    }
  };


  const getPlanCtaLabel = (plan) => {
    const label = planDisplayLabel(plan.code);
    if (isProcessing && processingPlanCode === plan.code) {
      return "Saving...";
    }

    if (isPaidActiveState) {
      if (isCancelingAtPeriodEnd) {
        return "Subscription Ending";
      }
      const currentCycle = subscriptionStatusMeta?.billingCycle;
      if (
        (plan.key === "monthly" && currentCycle === "Monthly") ||
        (plan.key === "yearly" && currentCycle === "Yearly")
      ) {
        return `${label} · Current Plan`;
      }
      return `Switch to ${label}`;
    }

    if (!isAuthenticated || isTrialEligibleState) {
      return plan.key === "monthly" ? "Subscribe Monthly" : "Subscribe Yearly";
    }

    if (isTrialEligibleState) {
      return plan.key === "monthly" ? "Subscribe Monthly" : "Subscribe Yearly";
    }
    if (isTrialActiveOrEndingState) {
      if (selectedTrialEndPlanCode === plan.code) {
        return `${label} selected`;
      }
      if (selectedTrialEndPlanCode) {
        return `Change to ${label}`;
      }
      return plan.key === "monthly" ? "Continue Monthly After Trial" : "Continue Yearly After Trial";
    }
    return plan.key === "monthly" ? "Start Monthly Plan" : "Start Yearly Plan";
  };

  const modalConfig =
    modalState === "auth_required"
      ? {
        title: "Login required",
        description: "Please log in to start your free trial or manage your subscription.",
        actions: [
          { label: "Close", onClick: () => setModalState(null) },
          { label: "Log in", onClick: handleLogin, variant: "primary" },
        ],
      }
      : modalState === "confirm_trial_plan_selection"
        ? {
          title: `${planDisplayLabel(pendingTrialPlanCode)} plan for trial end`,
          description: "Your card will not be charged today.",
          children: (() => {
            const plan = PLAN_OPTIONS.find((p) => p.code === pendingTrialPlanCode);
            return (
              <>
                <p>
                  The <strong>{plan?.price} {plan?.suffix}</strong> plan will begin when your free trial ends.
                </p>
                <div
                  style={{
                    marginTop: "14px",
                    padding: "10px 14px",
                    borderRadius: "8px",
                    border: "1px solid var(--color-pl-border, #e2ddd8)",
                    backgroundColor: "var(--color-pl-bg-subtle, #faf8f6)",
                    fontSize: "13px",
                    lineHeight: "1.55",
                    color: "var(--color-pl-text-secondary, #6b6259)",
                    display: "flex",
                    gap: "8px",
                    alignItems: "flex-start",
                  }}
                >
                  <span style={{ flexShrink: 0, fontSize: "15px" }}>ℹ️</span>
                  <span>
                    PayHere may apply a refundable LKR 10 authorization to verify your card. This is not a charge and will be reversed automatically.
                  </span>
                </div>
              </>
            );
          })(),


          actions: [
            {
              label: "Cancel",
              onClick: () => {
                setPendingTrialPlanCode(null);
                setModalState(null);
              },
            },
            {
              label: selectedTrialEndPlanCode ? "Confirm change" : "Confirm plan",
              variant: "primary",
              onClick: () => {
                const planCode = pendingTrialPlanCode;
                setModalState(null);
                if (planCode) {
                  handleStartSubscription(planCode, { confirmedTrialSelection: true });
                }
              },
            },
          ],
        }
        //=====================================================================================
        : modalState === "confirm_direct_subscription"
          ? {
            title: `Subscribe to ${planDisplayLabel(pendingDirectPlanCode)} Plan`,
            description: "Your subscription will begin immediately.",
            children: (() => {
              const plan = PLAN_OPTIONS.find((p) => p.code === pendingDirectPlanCode);
              return (
                <>
                  <p>
                    You are about to start the <strong>{plan?.planName}</strong>. Your card will be charged <strong>{plan?.price}</strong> today to activate your subscription.
                  </p>
                  <div
                    style={{
                      marginTop: "14px",
                      padding: "10px 14px",
                      borderRadius: "8px",
                      border: "1px solid var(--color-pl-border, #e2ddd8)",
                      backgroundColor: "var(--color-pl-bg-subtle, #faf8f6)",
                      fontSize: "13px",
                      lineHeight: "1.55",
                      color: "var(--color-pl-text-secondary, #6b6259)",
                      display: "flex",
                      gap: "8px",
                      alignItems: "flex-start",
                    }}
                  >
                    <span style={{ flexShrink: 0, fontSize: "15px" }}>ℹ️</span>
                    <span>
                      PayHere may apply a refundable LKR 10 authorization to verify your card. This is not a charge and will be reversed automatically.
                    </span>
                  </div>
                </>
              );
            })(),
            actions: [
              {
                label: "Cancel",
                onClick: () => {
                  setPendingDirectPlanCode(null);
                  setModalState(null);
                },
              },
              {
                label: "Confirm & Pay",
                variant: "primary",
                onClick: () => {
                  const planCode = pendingDirectPlanCode;
                  setModalState(null);
                  if (planCode) {
                    handleStartSubscription(planCode, { confirmedDirectSubscription: true });
                  }
                },
              },
            ],
          }
          //=====================================================================================
          : modalState === "payment_error"
            ? {
              title: "Payment initialization failed",
              description: errorMessage || "Unable to start payment. Please try again.",
              actions: [
                { label: "Close", onClick: () => setModalState(null), variant: "primary" },
              ],
            }
            : modalState === "trial_error"
              ? {
                title: "Trial start failed",
                description: errorMessage || "Unable to start trial. Please try again.",
                actions: [
                  { label: "Close", onClick: () => setModalState(null), variant: "primary" },
                ],
              }
              : modalState === "confirm_start_trial"
                ? {
                  title: "Confirm Trial Activation",
                  description: "Are you ready to start your 30-day free trial? Your access will begin immediately.",
                  actions: [
                    { label: "Cancel", onClick: () => setModalState(null) },
                    {
                      label: "Start Trial",
                      onClick: () => {
                        setModalState(null);
                        handleStartTrial();
                      },
                      variant: "primary"
                    },
                  ],
                }
                : null;

  return (
    <MainLayout>
      <Helmet>
        <title>Professional Plan | paralegal.lk</title>
        <meta
          name="description"
          content="Start a free trial of Paralegal’s dedicated chat workspace for AI-assisted legal research, drafting, and analysis."
        />
        <link rel="canonical" href="https://www.paralegal.lk/chat" />
      </Helmet>

      <article className="chat-pricing-page w-full max-w-[1080px] mx-auto px-6 md:px-12 py-10 md:py-16 text-pl-text-primary">
        {/* Masthead row */}
        {/* <header className="flex flex-wrap justify-between items-baseline gap-y-2 pb-4 mb-10 md:mb-14 border-b border-pl-border font-mono text-10 md:text-11 uppercase tracking-[0.22em] text-pl-text-tertiary"> */}
        {/* <header className="flex flex-wrap justify-between items-baseline gap-y-2 pb-4 mb-10 md:mb-14 font-mono text-10 md:text-11 uppercase tracking-[0.22em] text-pl-text-tertiary">
          <div className="flex items-baseline gap-3">
            <span className="text-pl-accent">“</span>
            <span>{chatDomainLabel}</span>
          </div>
          <span>Subscription workspace</span>
        </header> */}

        {/* Hero */}
        <div className="mb-12 md:mb-16">
          <h1 className="font-display leading-[1.02] tracking-[-0.015em] text-[42px] sm:text-[56px] md:text-[74px] lg:text-[82px] mb-6 reveal r-1">
            AI legal research and drafting
            <br />
            for Sri Lankan <em className="italic text-pl-accent">practice</em>.
          </h1>
          <p className="font-display text-18 md:text-21 leading-[1.52] text-pl-text-secondary max-w-[46ch] mb-8 reveal r-2">
            <span className="text-pl-accent underline underline-offset-4">
              {chatDomainLabel}
            </span>{" "}
            is our subscription workspace for legal research, drafting,
            and analysis. Work from the first question to a written output in
            one environment.
          </p>
          {/*place changed*/}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 reveal r-3">
            {planPrimaryLabel && (
              planPrimaryHref ? (
                <a
                  href={planPrimaryHref}
                  className="group inline-flex items-center gap-3 px-7 py-4 bg-pl-text-primary text-pl-bg rounded-pl font-mono text-12 md:text-15 uppercase tracking-[0.2em] hover:bg-pl-accent transition-colors duration-200"
                >
                  {planPrimaryLabel}
                  <span className="transition-transform duration-200 group-hover:translate-x-1">
                    →
                  </span>
                </a>
              ) : (
                <button
                  type="button"
                  onClick={planPrimaryAction || undefined}
                  disabled={planPrimaryDisabled}
                  aria-label={planPrimaryAriaLabel}
                  className={`group inline-flex items-center gap-3 px-7 py-4 bg-pl-text-primary text-pl-bg rounded-pl font-mono text-12 md:text-15 uppercase tracking-[0.2em] transition-colors duration-200 ${planPrimaryDisabled ? "cursor-not-allowed opacity-70" : "hover:bg-pl-accent"
                    }`}
                >
                  {planPrimaryLabel}
                  {!planPrimaryDisabled && (
                    <span className="transition-transform duration-200 group-hover:translate-x-1">
                      →
                    </span>
                  )}
                </button>
              )
            )}
          </div>

          {(!isAuthenticated || isTrialEligibleState) && (
            <p className="mt-3 text-14 text-pl-text-secondary reveal r-3">
              No card required. One free trial per account.
            </p>
          )}

        </div>

        {/* § I — The plan (conversion core) */}
        <SectionLabel n="I" label="The plan" />

        <div className="reveal r-4 mb-20 md:mb-28 rounded-pl-lg border border-pl-border bg-pl-bg-elevated shadow-pl-md overflow-hidden">
          <div className="flex flex-wrap justify-between items-baseline gap-2 px-6 md:px-8 py-4 border-b border-pl-border font-mono text-10 md:text-11 uppercase tracking-[0.2em] text-pl-text-tertiary">
            <span className="text-pl-accent">paralegal.lk Professional Plan</span>
            {/* <span>Monthly or yearly access</span> */}
          </div>

          {(showInlineStatusBadge || planHelperText || showAccountDetailsLink) && (
            <div className="p-6 md:p-8 border-b border-pl-border bg-pl-bg-subtle">
              <div className="flex flex-col md:flex-row md:justify-between gap-4 md:gap-6">
                <div className="flex flex-col justify-between gap-3">
                  {showInlineStatusBadge && (
                    <span className="inline-flex items-center gap-2 self-start rounded-full border border-pl-accent/30 bg-white/80 px-3 py-1 font-mono text-10 uppercase tracking-[0.14em] text-pl-accent">
                      {status === 'trial_pending_activation' && (
                        <svg
                          className="h-3 w-3 animate-spin text-pl-accent"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                        </svg>
                      )}
                      {inlineStatusText}
                    </span>
                  )}

                  {planHelperText && (
                    <p className="text-14 leading-[1.6] text-pl-text-secondary max-w-[62ch]">
                      {planHelperText}
                    </p>
                  )}

                </div>
                {showAccountDetailsLink && (
                  <div className="flex flex-col items-center justify-center gap-3 shrink-0 w-full md:w-auto mt-3 md:mt-0">
                    <Link
                      to="/my-account"
                      className="inline-flex font-mono text-12 md:text-13 uppercase tracking-[0.18em] text-pl-accent hover:text-pl-accent-hover transition-colors duration-200"
                    >
                      Subscription details →
                    </Link>
                  </div>
                )}

              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2">
            {PLAN_OPTIONS.map((plan, index) => {
              // const isSelectedTrialEndPlan = isTrialActiveOrEndingState && selectedTrialEndPlanCode === plan.code;
              const isSelectedTrialEndPlan = isTrialActiveOrEndingState && selectedTrialEndPlanCode === plan.code;
              const isCurrentPaidPlan = isPaidActiveState && (
                (plan.key === "monthly" && subscriptionStatusMeta?.billingCycle === "Monthly") ||
                (plan.key === "yearly" && subscriptionStatusMeta?.billingCycle === "Yearly")
              );
              const planActionDisabled = subscriptionActionDisabled || isSelectedTrialEndPlan || isCurrentPaidPlan;
              return (
                <div
                  key={plan.code}
                  className={`relative p-6 md:p-8 ${index === 0 ? "border-b md:border-b-0 md:border-r" : ""} ${plan.highlight
                    ? "border-pl-accent/40 bg-[rgba(122,63,44,0.035)] shadow-[inset_0_0_0_1px_rgba(122,63,44,0.18)]"
                    : "border-pl-border"
                    } flex flex-col`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                    <div>
                      <p className="font-mono text-10 uppercase tracking-[0.2em] text-pl-text-tertiary">
                        {plan.billingLabel}
                      </p>
                      <h2 className="mt-2 font-display text-24 md:text-30 leading-[1.15] text-pl-text-primary">
                        {plan.planName}
                      </h2>
                    </div>
                    {(plan.badge || plan.valueLabel) && (
                      <div className="flex flex-wrap justify-end gap-2">
                        {plan.valueLabel && (
                          <span className="rounded-full border border-pl-border bg-white/70 px-3 py-1 font-mono text-10 uppercase tracking-[0.16em] text-pl-text-tertiary">
                            {plan.valueLabel}
                          </span>
                        )}
                        {plan.badge && (
                          <span className="rounded-full border border-pl-accent/30 bg-[rgba(122,63,44,0.08)] px-3 py-1 font-mono text-10 uppercase tracking-[0.16em] text-pl-accent">
                            {plan.badge}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex items-end gap-3 mb-4">
                    <span className="font-display text-[40px] md:text-[50px] leading-none tracking-[-0.02em]">
                      {plan.price}
                    </span>
                    <span className="pb-2 font-mono text-11 uppercase tracking-[0.18em] text-pl-text-tertiary">
                      {plan.suffix}
                    </span>
                  </div>

                  {plan.comparison && (
                    <p className="mb-2 text-13 font-semibold leading-[1.6] text-pl-accent">
                      {plan.comparison}
                    </p>
                  )}

                  {plan.valueNote && (
                    <p className="mb-4 text-13 leading-[1.6] text-pl-text-secondary">
                      {plan.valueNote}
                    </p>
                  )}

                  <p className="text-15 leading-[1.7] text-pl-text-secondary mb-6">
                    {plan.description}
                  </p>

                  <p className="font-mono text-10 uppercase tracking-[0.2em] text-pl-text-tertiary mb-4">
                    What’s included
                  </p>
                  <ul className="space-y-3 mb-7">
                    {INCLUDED_ITEMS.map((item) => (
                      <li
                        key={`${plan.key}-${item}`}
                        className="flex gap-3 text-15 leading-[1.5] text-pl-text-primary"
                      >
                        <span className="text-pl-accent pt-[2px]" aria-hidden="true">
                          ✓
                        </span>
                        <span>
                          {item === "Access to chat.paralegal.lk" ? (
                            <>
                              Access to{" "}
                              <span className="text-pl-accent underline underline-offset-4">
                                {chatDomainLabel}
                              </span>
                            </>
                          ) : (
                            item
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* {showPlanCheckoutActions && ( */}
                  {showPlanCheckoutActions && !isTrialEligibleState && (
                    <button
                      type="button"
                      onClick={() => handleStartSubscription(plan.code)}
                      disabled={planActionDisabled}
                      aria-label={`${getPlanCtaLabel(plan)} for ${plan.billingLabel.toLowerCase()} billing`}
                      className={`group mt-auto inline-flex w-full items-center justify-center gap-3 px-6 py-3.5 rounded-pl font-mono text-10 md:text-11 uppercase tracking-[0.18em] text-white transition-colors duration-200 ${planActionDisabled
                        ? "cursor-not-allowed bg-pl-text-tertiary"
                        : "bg-pl-accent hover:bg-pl-accent-hover"
                        }`}
                    >
                      {getPlanCtaLabel(plan)}
                      {!planActionDisabled && (
                        <span className="transition-transform duration-200 group-hover:translate-x-1">
                          →
                        </span>
                      )}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {(isTrialEligibleState || isTrialActiveOrEndingState || isPaidActiveState || isPendingActivationState) && (
            <div className="border-t border-pl-border p-6 md:p-8 flex flex-col items-center gap-3">
              {isTrialEligibleState && (
                <>
                  <button
                    type="button"
                    onClick={planPrimaryAction || undefined}
                    disabled={planPrimaryDisabled}
                    aria-label={planPrimaryAriaLabel}
                    className={`group inline-flex items-center gap-3 px-7 py-4 bg-pl-text-primary text-pl-bg rounded-pl font-mono text-12 md:text-15 uppercase tracking-[0.2em] transition-colors duration-200 ${planPrimaryDisabled ? "cursor-not-allowed opacity-70" : "hover:bg-pl-accent"
                      }`}
                  >
                    {planPrimaryLabel}
                    {!planPrimaryDisabled && (
                      <span className="transition-transform duration-200 group-hover:translate-x-1">
                        →
                      </span>
                    )}
                  </button>
                  <p className="text-14 text-pl-text-secondary">
                    No card required. One free trial per account.
                  </p>
                </>
              )}
              {isPendingActivationState && (
                <button
                  type="button"
                  disabled={true}
                  aria-label="Activating trial access"
                  className="group inline-flex items-center gap-3 px-7 py-4 bg-pl-text-primary text-pl-bg rounded-pl font-mono text-12 md:text-15 uppercase tracking-[0.2em] cursor-not-allowed opacity-70"
                >
                  Activating...
                </button>
              )}
              {(isTrialActiveOrEndingState || isPaidActiveState) && (
                <a
                  href={chatUrl}
                  className="group inline-flex items-center gap-3 px-7 py-4 bg-pl-text-primary text-pl-bg rounded-pl font-mono text-12 md:text-15 uppercase tracking-[0.2em] hover:bg-pl-accent transition-colors duration-200"
                >
                  Open Chat
                  <span className="transition-transform duration-200 group-hover:translate-x-1">
                    →
                  </span>
                </a>
              )}
            </div>
          )}

          <div className="border-t border-pl-border bg-pl-bg-subtle p-6 md:p-8">
            <p className="text-13 leading-[1.6] text-pl-text-tertiary">
              Subscription card approval is handled through PayHere. Direct subscriptions charge immediately; trial-active accounts are charged at trial end; expired trials restore access only after a confirmed charge and access activation.
            </p>
            <p className="mt-3 text-12 leading-[1.6] text-pl-text-tertiary">
              By continuing, you agree to our{" "}
              <Link to="/terms" className="text-pl-accent hover:underline">
                Terms
              </Link>
              ,{" "}
              <Link to="/privacy" className="text-pl-accent hover:underline">
                Privacy Policy
              </Link>
              , and{" "}
              <Link to="/refund" className="text-pl-accent hover:underline">
                Refund Policy
              </Link>
              .
            </p>
          </div>
        </div>

        {/* § II — What it does */}
        <SectionLabel n="II" label="What it does" />

        <div className="space-y-14 md:space-y-20 mb-20 md:mb-28">
          {PRODUCT_FEATURES.map((feature, index) => (
            <FeatureArticle
              key={feature.n}
              {...feature}
              revealClass={`cap-reveal cr-${index + 1}`}
            />
          ))}
        </div>

        {/* § III — Firms and teams */}
        <SectionLabel n="III" label="Firms and teams" />

        <div className="rounded-pl-lg border border-pl-border bg-pl-bg-elevated p-6 md:p-8">
          <h2 className="font-display text-28 md:text-34 leading-[1.15] text-pl-text-primary mb-4">
            Access for your firm or team.
          </h2>
          <p className="text-15 md:text-16 leading-[1.75] text-pl-text-secondary max-w-[60ch] mb-6">
            For chambers, law firms, and legal teams that need multi-user
            access, shared onboarding, and tailored support, choose a group
            subscription. Per-seat pricing falls as the team grows.
          </p>

          <ul className="space-y-3 mb-8 max-w-[60ch]">
            {GROUP_PLAN_HIGHLIGHTS.map((item) => (
              <li
                key={item}
                className="flex gap-3 text-15 leading-[1.5] text-pl-text-primary"
              >
                <span className="text-pl-accent pt-[2px]" aria-hidden="true">
                  ✓
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>

          <a
            href="mailto:admin@paralegal.lk?subject=Group%20Subscription%20Inquiry"
            className="group inline-flex items-center gap-3 px-7 py-4 bg-pl-text-primary text-pl-bg rounded-pl font-mono text-11 md:text-12 uppercase tracking-[0.2em] hover:bg-pl-accent transition-colors duration-200"
          >
            Contact us
            <span className="transition-transform duration-200 group-hover:translate-x-1">
              →
            </span>
          </a>
        </div>

        {/* Page-scoped styles */}
        <style>{`
          .chat-pricing-page .reveal { opacity: 0; transform: translateY(14px); animation: cp-revealUp .85s cubic-bezier(.2,.7,.2,1) forwards; }
          .chat-pricing-page .r-1 { animation-delay: .04s; }
          .chat-pricing-page .r-2 { animation-delay: .14s; }
          .chat-pricing-page .r-3 { animation-delay: .24s; }
          .chat-pricing-page .r-4 { animation-delay: .34s; }
          @keyframes cp-revealUp { to { opacity: 1; transform: translateY(0); } }

          .chat-pricing-page .cap-reveal { opacity: 0; transform: translateY(14px); animation: cp-capIn .85s cubic-bezier(.2,.7,.2,1) forwards; }
          .chat-pricing-page .cr-1 { animation-delay: .10s; }
          .chat-pricing-page .cr-2 { animation-delay: .18s; }
          .chat-pricing-page .cr-3 { animation-delay: .26s; }
          .chat-pricing-page .cr-4 { animation-delay: .34s; }
          .chat-pricing-page .cr-5 { animation-delay: .42s; }
          @keyframes cp-capIn { to { opacity: 1; transform: translateY(0); } }

          @media (prefers-reduced-motion: reduce) {
            .chat-pricing-page .reveal,
            .chat-pricing-page .cap-reveal { animation: none; opacity: 1; transform: none; }
          }
        `}</style>
      </article>

      <DialogModal
        isOpen={Boolean(modalConfig)}
        onClose={() => setModalState(null)}
        title={modalConfig?.title}
        description={modalConfig?.description}
        actions={modalConfig?.actions}
      >
        {modalConfig?.children}
      </DialogModal>

      {/* add new endpoint for manually change the plan */}
      {/* Plan Change Confirmation Modal */}
      {planChangeState && (
        <DialogModal
          isOpen={true}
          onClose={() => {
            if (!isChangingPlan) {
              setPlanChangeState(null);
            }
          }}
          title={`Switch to ${planChangeState.planName}`}
          description={`Your plan will change to ${planChangeState.planName} at your next renewal. No immediate prorated charge will be made.`}
          actions={[
            {
              label: "Cancel",
              onClick: () => {
                if (!isChangingPlan) {
                  setPlanChangeState(null);
                }
              },
            },
            {
              label: isChangingPlan ? "Scheduling..." : "Schedule Change",
              onClick: () => {
                if (!isChangingPlan) {
                  executePlanChange();
                }
              },
              variant: "primary",
            },
          ]}
        />
      )}

    </MainLayout>
  );
};

// const SectionLabel = ({ n, label }) => (
//   <div className="relative border-t border-pl-border mt-12 mb-10 md:mb-14">
//     <div className="absolute -top-[10px] left-0 bg-pl-bg pr-4 font-mono text-10 md:text-11 uppercase tracking-[0.22em] text-pl-text-tertiary flex items-baseline gap-3">
//       <span className="text-pl-accent">§ {n}</span>
//       <span>{label}</span>
//     </div>
//   </div>
// );
const SectionLabel = ({ n, label }) => (
  <div className="mt-12 mb-10 md:mb-14 font-mono text-10 md:text-11 uppercase tracking-[0.22em] text-pl-text-tertiary flex items-baseline gap-3">
    <span className="text-pl-accent">§ {n}</span>
    <span>{label}</span>
  </div>
);

const FeatureArticle = ({ n, tag, title, description, revealClass }) => (
  <section
    className={`grid grid-cols-1 md:grid-cols-[140px_1fr] gap-y-3 gap-x-10 md:gap-x-16 items-start ${revealClass}`}
  >
    <div className="font-mono uppercase tracking-[0.22em] pt-1 flex md:block items-baseline gap-3">
      <span className="text-11 text-pl-accent">¶ {n}</span>
      <span className="block text-pl-text-tertiary text-10 md:mt-2">{tag}</span>
    </div>
    <div>
      <h3 className="font-display text-24 md:text-32 leading-[1.18] text-pl-text-primary mb-4 max-w-[28ch] tracking-[-0.005em]">
        {title}
      </h3>
      <p className="text-15 md:text-16 leading-[1.78] text-pl-text-secondary max-w-[58ch]">
        {description}
      </p>
    </div>
  </section>
);

export default ChatPricing;